import type { AbstractAnalyticsDispatcher } from "@asurion-core/eventualize";
import type { Partner } from "@expert/shared-types";

import { buildDispatcher, type KnownPlatforms } from "@asurion-core/eventualize";
import { reactAnalytics } from "@asurion-core/eventualize-react";

import type { Actions, ApplicationEvents } from "./eventTypes";

import analyticsApiSink from "./analyticsApiSink";
import { releaseFeatureFlagReports } from "./featureFlagReporter";

const isProd = import.meta.env.VITE_ENV_TYPE === "production" && import.meta.env.PROD;
const analyticsPlatform = import.meta.env.VITE_ANALYTICS_PLATFORM as KnownPlatforms;

/** Our main dispatcher instance, it should not be accessed directly */
let rootDispatcher = buildDispatcher(
    {
        platform: analyticsPlatform,
        appPath: "/",
        appName: "expert-workspace-platform",
        isProd,
        partner: "asurion" /** We don't know the partner until the worker logs in to the workspace */,
    },
    [analyticsApiSink],
)
    .typeActions<Actions>()
    .typeEvents<ApplicationEvents>()
    .getDispatcher();

export type RootDispatcherType = typeof rootDispatcher;

// Critical to initialize react analytics with the root dispatcher
reactAnalytics.init(rootDispatcher);
reactAnalytics.onChange.on(onDispatcherChange);

/** This ensures that the root dispatcher is always up-to-date with the source-of-truth reactAnalytics */
function onDispatcherChange(dispatcher: RootDispatcherType) {
    rootDispatcher = dispatcher;
}

export function getRootDispatcher(): RootDispatcherType {
    return reactAnalytics.dispatcher;
}

/**
 * Marks the current user as a test user and skips sending events to EDP.
 * This prevents test/dev events from polluting production analytics.
 * All subsequent events skip EDP when this is called
 */
export function setIdentityAsTestUser(): void {
    reactAnalytics.transformDispatcher((dispatcher) => dispatcher.skipEdp().withIdentity("TestUser", "true"));
}

export function updateDispatcherPartner(newPartner: Partner) {
    reactAnalytics.transformDispatcher((dispatcher) => dispatcher.withExtra({ partner: newPartner }));
    // Release the flag values held through start-up, now stamped with the partner; a no-op after the first time.
    // `getPartners()` can be empty when the SDK loads, so a missing partner releases nothing.
    if (newPartner) releaseFeatureFlagReports();
}

/** Simple wrapper around base lib transformDispatcher for use directly */
export function transformDispatcher<TIn extends AbstractAnalyticsDispatcher, TOut extends AbstractAnalyticsDispatcher>(
    transformFunc: (dispatcher: TIn) => TOut,
) {
    reactAnalytics.transformDispatcher(transformFunc);
}
